import { UnknownElementPlaceholder } from './unknown-element-placeholder.js'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { JSX } from 'react'
import type { RenderElementProps } from 'slate-react'

/**
 * Fallback `renderElement` for the editor's `<Plate>` component.
 *
 * Plate runs each plugin's element renderer in order. When no plugin claims the
 * element's `type`, this fallback fires (instead of Plate's default
 * `<PlateElement>` `<div>`). For unregistered types we render a visible
 * UnknownElementPlaceholder so the user (and devs) can see something is there
 * — instead of silently dropping the data.
 *
 * For elements that arrive here without being claimed by a plugin but whose
 * type IS registered (rare — usually means the plugin omitted `node.component`),
 * we render a neutral wrapper that mirrors Plate's default behavior.
 */
export const renderUnknownElement =
  (editor: TunaEditor) =>
  (props: RenderElementProps): JSX.Element => {
    const { element, attributes, children } = props
    const type = (element as { type?: unknown }).type
    const isUnknown = (editor as { isUnknownType?: (t: string) => boolean }).isUnknownType
    if (typeof type === 'string' && type.length > 0 && isUnknown && isUnknown(type)) {
      return (
        <span {...attributes}>
          <UnknownElementPlaceholder type={type}>{children}</UnknownElementPlaceholder>
        </span>
      )
    }
    // Registered type but no plugin renderer claimed it (rare). Render a neutral
    // wrapper that preserves slate-react's `attributes` so node bookkeeping stays
    // intact.
    const isInline = attributes['data-slate-inline'] === true
    if (isInline) {
      return <span {...attributes}>{children}</span>
    }
    return <div {...attributes}>{children}</div>
  }
